import React, { useState, useEffect, useRef } from 'react';
import { Settings, Pencil, Eraser, Trash2, Undo, Circle } from 'lucide-react';
import { Encounter } from '../../types/encounter';
import { EncounterSectionTemplate, QuestionType } from '../../types/encounterTemplate';
import { getTemplatesForSection } from '../../utils/encounterTemplateApi';
import { updateEncounter } from '../../utils/encounterApi';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import TemplateEditorModal from './TemplateEditorModal';

interface BodyMapSectionProps {
  encounter: Encounter | null;
  onUpdate: () => void;
}

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

const BodyMapSection: React.FC<BodyMapSectionProps> = ({ encounter, onUpdate }) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<EncounterSectionTemplate[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [petSpecies, setPetSpecies] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoadingPet, setIsLoadingPet] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Drawing state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingTool, setDrawingTool] = useState<'pen' | 'eraser' | 'circle'>('pen');
  const [penColor, setPenColor] = useState('#FF0000');
  const [penSize, setPenSize] = useState(3);
  const [drawingHistory, setDrawingHistory] = useState<string[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const drawingSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasUnsavedDrawing, setHasUnsavedDrawing] = useState(false);
  const currentAnnotationsRef = useRef<string | undefined>(undefined);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHoveringCanvas, setIsHoveringCanvas] = useState(false);

  // Sync form data with encounter (but preserve drawing state)
  useEffect(() => {
    if (encounter) {
      const newData = encounter.body_map_data || {};
      
      setFormData(newData);
      
      // Only reload annotations if they're different from what we have locally
      // This prevents resetting the canvas while user is drawing
      if (newData.annotations && 
          newData.annotations !== currentAnnotationsRef.current && 
          canvasRef.current) {
        loadAnnotationToCanvas(newData.annotations);
        currentAnnotationsRef.current = newData.annotations;
      }
    }
  }, [encounter]);

  // Fetch pet species
  useEffect(() => {
    const fetchPetSpecies = async () => {
      if (!encounter?.pet_id) return;

      setIsLoadingPet(true);
      try {
        const response = await fetch(API_ENDPOINTS.PETS.GET(encounter.pet_id));
        if (response.ok) {
          const pet: Pet = await response.json();
          setPetSpecies(pet.species?.toLowerCase() || '');
        }
      } catch (error) {
        console.error('Failed to fetch pet species:', error);
      } finally {
        setIsLoadingPet(false);
      }
    };

    fetchPetSpecies();
  }, [encounter?.pet_id]);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!user?.practice_id) return;

      setIsLoadingTemplates(true);
      try {
        const data = await getTemplatesForSection(user.practice_id, 'body_map');
        setTemplates(data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [user?.practice_id]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (drawingSaveTimeoutRef.current) {
        clearTimeout(drawingSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleFieldChange = (questionKey: string, value: any) => {
    const updatedData = { ...formData, [questionKey]: value };
    setFormData(updatedData);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new autosave timeout (1 second debounce)
    if (encounter) {
      saveTimeoutRef.current = setTimeout(async () => {
        setIsSaving(true);
        try {
          await updateEncounter(encounter.pet_id, encounter.id, {
            body_map_data: updatedData,
          });
          onUpdate();
        } catch (error) {
          console.error('Failed to save body map data:', error);
        }
        // Show "Saving..." for at least 800ms for visual feedback
        setTimeout(() => setIsSaving(false), 800);
      }, 1000);
    }
  };

  const handleTemplatesUpdate = async () => {
    if (!user?.practice_id) return;
    const data = await getTemplatesForSection(user.practice_id, 'body_map');
    setTemplates(data);
  };

  const renderField = (template: EncounterSectionTemplate) => {
    const value = formData[template.question_key] || '';

    switch (template.question_type) {
      case 'text':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ minHeight: '60px' }}
            placeholder={template.is_required ? 'Required' : 'Optional'}
          />
        );

      case 'yes_no':
        return (
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name={template.question_key}
                value="yes"
                checked={value === 'yes'}
                onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={template.question_key}
                value="no"
                checked={value === 'no'}
                onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
                className="mr-2"
              />
              No
            </label>
          </div>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {template.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {template.options?.map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter((v: string) => v !== option);
                    handleFieldChange(template.question_key, newValues);
                  }}
                  className="mr-2"
                />
                {option}
              </label>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={template.is_required ? 'Required' : 'Optional'}
          />
        );

      default:
        return null;
    }
  };

  const getBodyMapImage = () => {
    // Determine which image to show based on species
    if (petSpecies.includes('cat') || petSpecies.includes('feline')) {
      return '/cat.jpg';
    } else if (petSpecies.includes('dog') || petSpecies.includes('canine')) {
      return '/dog.jpg';
    }
    // Default to dog if species is unknown or other
    return '/dog.jpg';
  };

  // Initialize canvas when image loads
  const handleImageLoad = () => {
    if (!canvasRef.current || !imageRef.current) return;
    
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    // Set canvas size to match image display size
    canvas.width = image.offsetWidth;
    canvas.height = image.offsetHeight;
    
    // Load saved annotations if they exist
    if (encounter?.body_map_data?.annotations) {
      loadAnnotationToCanvas(encounter.body_map_data.annotations);
    }
  };

  const loadAnnotationToCanvas = (dataUrl: string) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = dataUrl;
  };

  const saveAnnotation = () => {
    if (!canvasRef.current || !encounter) return;
    
    const dataUrl = canvasRef.current.toDataURL('image/png');
    const updatedData = { 
      ...formData, 
      annotations: dataUrl 
    };
    
    // Update local state immediately
    setFormData(updatedData);
    setHasUnsavedDrawing(true);
    currentAnnotationsRef.current = dataUrl; // Track current local state
    
    // Clear existing timeout
    if (drawingSaveTimeoutRef.current) {
      clearTimeout(drawingSaveTimeoutRef.current);
    }
    
    // Debounce the backend save (2 seconds after last drawing action)
    drawingSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await updateEncounter(encounter.pet_id, encounter.id, {
          body_map_data: updatedData,
        });
        setHasUnsavedDrawing(false);
        // Don't call onUpdate() to avoid re-rendering and resetting state
      } catch (error) {
        console.error('Failed to save annotation:', error);
      }
    }, 2000);
  };

  const saveToHistory = () => {
    if (!canvasRef.current) return;
    
    const dataUrl = canvasRef.current.toDataURL();
    const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
    newHistory.push(dataUrl);
    setDrawingHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      setCurrentHistoryIndex(newIndex);
      loadAnnotationToCanvas(drawingHistory[newIndex]);
    }
  };

  const handleClearAll = () => {
    setShowClearConfirm(true);
  };

  const confirmClearAll = async () => {
    if (!canvasRef.current || !encounter) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawingHistory([]);
    setCurrentHistoryIndex(-1);
    
    // Clear annotations and save immediately
    const updatedData = { 
      ...formData, 
      annotations: undefined 
    };
    setFormData(updatedData);
    currentAnnotationsRef.current = undefined; // Track cleared state
    
    try {
      await updateEncounter(encounter.pet_id, encounter.id, {
        body_map_data: updatedData,
      });
    } catch (error) {
      console.error('Failed to clear annotations:', error);
    }
    
    setShowClearConfirm(false);
  };

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    setMousePos(pos);
    
    if (isDrawing) {
      draw(e);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    setIsDrawing(true);
    const pos = getMousePos(e);
    
    if (drawingTool === 'pen') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    } else if (drawingTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)'; // Color doesn't matter for eraser
      ctx.lineWidth = penSize * 5; // Larger eraser
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const pos = getMousePos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.globalCompositeOperation = 'source-over';
    }
    
    setIsDrawing(false);
    saveToHistory();
    saveAnnotation();
  };

  if (!encounter) {
    return (
      <div className="text-center py-8 text-gray-500">
        No encounter selected
      </div>
    );
  }

  if (isLoadingTemplates || isLoadingPet) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Body Map</h2>
        <button
          onClick={() => setIsEditorOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <Settings className="w-4 h-4" />
          Edit Template
        </button>
      </div>

      {/* Body Map Image with Drawing Tools */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        {/* Drawing Toolbar */}
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
          <button
            onClick={() => setDrawingTool('pen')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
              drawingTool === 'pen'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            title="Pen tool"
          >
            <Pencil className="w-4 h-4" />
            Pen
          </button>
          
          <button
            onClick={() => setDrawingTool('eraser')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
              drawingTool === 'eraser'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            title="Eraser tool"
          >
            <Eraser className="w-4 h-4" />
            Eraser
          </button>
          
          <div className="h-6 w-px bg-gray-300"></div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Color:</label>
            <input
              type="color"
              value={penColor}
              onChange={(e) => setPenColor(e.target.value)}
              className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Size:</label>
            <input
              type="range"
              min="1"
              max="10"
              value={penSize}
              onChange={(e) => setPenSize(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-6">{penSize}</span>
          </div>
          
          <div className="h-6 w-px bg-gray-300"></div>
          
          <button
            onClick={handleUndo}
            disabled={currentHistoryIndex <= 0}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Undo last action"
          >
            <Undo className="w-4 h-4" />
            Undo
          </button>
          
          <button
            onClick={handleClearAll}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
            title="Clear all annotations"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
        
        {/* Image with Canvas Overlay */}
        <div className="flex justify-center mb-6">
          <div className="relative inline-block">
            <img
              ref={imageRef}
              src={getBodyMapImage()}
              alt={`${petSpecies || 'Pet'} body map`}
              className="max-w-full h-auto rounded-lg shadow-md"
              style={{ maxHeight: '500px', display: 'block' }}
              onLoad={handleImageLoad}
            />
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={handleMouseMove}
              onMouseUp={stopDrawing}
              onMouseLeave={() => {
                stopDrawing();
                setIsHoveringCanvas(false);
              }}
              onMouseEnter={() => setIsHoveringCanvas(true)}
              className="absolute top-0 left-0 rounded-lg"
              style={{ 
                touchAction: 'none',
                cursor: drawingTool === 'pen' ? 'crosshair' : 'none'
              }}
            />
            
            {/* Adobe-style eraser cursor */}
            {drawingTool === 'eraser' && isHoveringCanvas && (
              <svg
                className="absolute pointer-events-none"
                style={{
                  left: mousePos.x - (penSize * 5) / 2,
                  top: mousePos.y - (penSize * 5) / 2,
                  filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))',
                }}
                width={penSize * 5}
                height={penSize * 5}
                viewBox={`0 0 ${penSize * 5} ${penSize * 5}`}
              >
                {/* Outer white stroke for visibility on dark backgrounds */}
                <circle 
                  cx={penSize * 5 / 2} 
                  cy={penSize * 5 / 2} 
                  r={penSize * 5 / 2 - 2} 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2.5"
                  opacity="0.9"
                />
                {/* Inner dark stroke */}
                <circle 
                  cx={penSize * 5 / 2} 
                  cy={penSize * 5 / 2} 
                  r={penSize * 5 / 2 - 2} 
                  fill="none" 
                  stroke="#333" 
                  strokeWidth="1.5"
                />
                {/* Center crosshair */}
                <line
                  x1={penSize * 5 / 2}
                  y1={penSize * 5 / 2 - 4}
                  x2={penSize * 5 / 2}
                  y2={penSize * 5 / 2 + 4}
                  stroke="#666"
                  strokeWidth="1"
                />
                <line
                  x1={penSize * 5 / 2 - 4}
                  y1={penSize * 5 / 2}
                  x2={penSize * 5 / 2 + 4}
                  y2={penSize * 5 / 2}
                  stroke="#666"
                  strokeWidth="1"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Template Questions */}
        <div className="space-y-4">
          {templates
            .sort((a, b) => a.display_order - b.display_order)
            .map((template, index) => (
              <div
                key={template.id}
                className={`grid grid-cols-[240px_1fr] gap-4 items-start ${
                  index > 0 ? 'pt-4 border-t border-gray-200' : ''
                }`}
              >
                <label className="text-sm font-medium text-gray-700 pt-2">
                  {template.question_text}
                  {template.is_required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div>{renderField(template)}</div>
              </div>
            ))}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No template questions configured. Click "Edit Template" to add questions.
          </div>
        )}
      </div>

      {/* Saving Indicator */}
      {(isSaving || hasUnsavedDrawing) && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          {hasUnsavedDrawing ? 'Saving drawing...' : 'Saving...'}
        </div>
      )}

      {/* Template Editor Modal */}
      {isEditorOpen && user?.practice_id && (
        <TemplateEditorModal
          practiceId={user.practice_id}
          sectionName="body_map"
          templates={templates}
          onClose={() => setIsEditorOpen(false)}
          onUpdate={() => {
            handleTemplatesUpdate();
            onUpdate();
          }}
        />
      )}

      {/* Clear Annotations Confirmation Modal */}
      {showClearConfirm && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowClearConfirm(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
              {/* Header with warning icon */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <svg 
                        className="w-6 h-6 text-yellow-600" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                        />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex-1 pt-0.5">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Clear All Annotations?
                    </h3>
                    <p className="text-sm text-gray-600">
                      This will permanently remove all drawings from the body map. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BodyMapSection;
